import { Autocomplete, Typography, TextField } from "@mui/material";
import { ChevronDown } from "../../icons/ChevronDown";
import "../../../assets/css/CustomAutoComplete.css";
import type { SyntheticEvent } from "react";
import { useEffect, useState } from "react";

interface CustomAutoCompleteProps<T extends { id: number }> {
  label: string;
  selectedValue: T | string | null;
  options: T[];
  labelKey: keyof T;
  heightOverride?: number | null;
  fullWidth?: boolean;
  handleSelectChange: (event: SyntheticEvent | null, newValue: T | string | null) => void;
}

const CustomAutoComplete = <T extends { id: number }>({
  label,
  selectedValue,
  options,
  labelKey,
  heightOverride = null,
  fullWidth,
  handleSelectChange,
}: CustomAutoCompleteProps<T>) => {
  const [inputValue, setInputValue] = useState<string>('');

  // Update input value when selectedValue changes from outside (like form reset)
  useEffect(() => {
    if (selectedValue == null) {
      setInputValue('');
    } else if (typeof selectedValue === 'string') {
      setInputValue(selectedValue);
    } else {
      setInputValue(String(selectedValue[labelKey]));
    }
  }, [selectedValue, labelKey]);

  return (
    <Autocomplete
      freeSolo
      className="autocomplete"
      id="autocomplete"
      popupIcon={<ChevronDown style={{ padding: "0px", alignItems: "center" }} />}
      options={options}
      value={selectedValue || null}
      inputValue={inputValue}
      fullWidth={Boolean(fullWidth)}
      getOptionLabel={(option: T | string) =>
        typeof option === "string" ? option : (option[labelKey] as string)
      }
      onChange={handleSelectChange}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onBlur={() => {
        // Ensure freeSolo text is persisted when user leaves the field.
        // This avoids relying on per-keystroke updates (which can race with option selection).
        const trimmed = inputValue.trim();
        const selectedLabel =
          selectedValue == null
            ? ''
            : typeof selectedValue === 'string'
              ? selectedValue.trim()
              : String((selectedValue as any)[labelKey] ?? '').trim();

        // No-op: user didn't meaningfully change the value.
        if (trimmed === selectedLabel) return;

        // If user cleared the input, clear selection.
        if (!trimmed) {
          handleSelectChange(null, null);
          return;
        }

        handleSelectChange(null, trimmed);
      }}
      renderOption={(props, option, index) => {
        const key = `listItem-${index}-${(option as T).id}`;
        return (
          <li {...props} key={key} className="autocomplete-option">
            <Typography className="autocomplete-option-text">
              {String((option as T)[labelKey])}
            </Typography>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: heightOverride || 32,
              fontSize: '12px',
              padding: 0,
              '& input': {
                padding: '6px 8px',
                height: '100%',
                boxSizing: 'border-box',
              },
              '& fieldset': {
                borderColor: undefined,
              },
              '&.Mui-focused fieldset': {
                borderColor: '#245EA0 !important',
              },
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.75rem',
              color: '#8E8E93 !important',
              '&.Mui-focused': {
                color: '#245EA0 !important',
              },
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );
};

export { CustomAutoComplete };
